import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Handler from "../Helpers/Handler";
import { createAvailability } from "../Helpers/carAvailability";
import { addHours, formatRangeDate } from "../Helpers/dateRange";
import BookingRangePicker from "./BookingRangePicker";

/**
 * BookingRangePicker for one specific car (Booking > Register).
 *
 * Same calendar as the Availability page, plus the car's booked windows
 * (GET /staff/flow/car/:carId/busy) greyed out: fully booked days can't be
 * picked, partly booked days show a marker with the booked times, and the
 * time sliders shade booked hours. The windows are re-fetched each time the
 * calendar opens so a booking made meanwhile shows up.
 *
 * If the current range clashes with a booking (e.g. the car was switched, or
 * dates came from a link), it is moved to the nearest free window and a note
 * says so. The server still re-checks at prebook, so a booking made after
 * this data loaded is caught there too.
 *
 * Props: carId, value, onChange, dates, disabled, id - as BookingRangePicker.
 */
const CarBookingRangePicker = ({ carId, value, onChange, dates, disabled = false, id }) => {
    const [busy, setBusy] = useState([]);
    const [status, setStatus] = useState(carId ? "loading" : "idle"); // idle | loading | ready | error
    const [note, setNote] = useState(null);
    const requestRef = useRef(0);

    const load = useCallback(async ({ quiet = false } = {}) => {
        if (!carId) { setBusy([]); setStatus("idle"); return; }
        const request = ++requestRef.current;
        if (!quiet) setStatus("loading");
        const now = new Date();
        // Everything a pickup (horizon) + the longest trip could reach, plus a day.
        const to = addHours(dates.maxEndFor(dates.latestStart(now)), 24);
        const params = new URLSearchParams({ from: now.toISOString(), to: to.toISOString() });
        const response = await Handler({ method: "get", url: `/staff/flow/car/${encodeURIComponent(carId)}/busy?${params.toString()}` });
        if (request !== requestRef.current) return; // a newer request (or car) superseded this one
        if (response?.success) {
            setBusy(response?.data?.data?.windows || []);
            setStatus("ready");
        } else if (!quiet) {
            setBusy([]);
            setStatus("error");
        }
    }, [carId, dates]);

    // New car: forget the old car's bookings straight away, then load.
    useEffect(() => {
        setBusy([]);
        setNote(null);
        load();
    }, [load]);

    const av = useMemo(() => createAvailability(dates, busy), [dates, busy]);

    // Keep the committed range clear of this car's bookings.
    useEffect(() => {
        if (status !== "ready" || !value?.start || !value?.end) return;
        if (av.isRangeFree(value)) return;
        const fit = av.fitRange(value);
        if (fit.blocked) {
            const text = "This car has no free window long enough for a trip in the bookable period. Choose another car.";
            // Functional + same-text guard: this effect re-runs on every render while blocked.
            setNote((prev) => (prev?.text === text ? prev : { tone: "danger", text }));
            return;
        }
        setNote({
            tone: "warning",
            text: `This car is already booked during the chosen dates, so they were moved to its nearest free window: ${formatRangeDate(fit.start)} – ${formatRangeDate(fit.end)} (IST).`,
        });
        onChange({ start: fit.start, end: fit.end });
    }, [status, av, value, onChange]);

    const handleChange = (next) => {
        setNote(null);
        onChange(next);
    };

    return (
        <>
            <BookingRangePicker
                id={id}
                dates={dates}
                value={value}
                onChange={handleChange}
                disabled={disabled || !carId}
                busy={busy}
                availabilityLoading={status === "loading"}
                onOpen={() => load({ quiet: status === "ready" })}
            />
            {status === "error" && (
                <div className="form-text text-danger">
                    Couldn&apos;t load this car&apos;s bookings, so booked dates aren&apos;t greyed out. The dates will still be checked when you register.{" "}
                    <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => load()}>Retry</button>
                </div>
            )}
            {note && <div className={`form-text text-${note.tone === "danger" ? "danger" : "warning-emphasis"}`}>{note.text}</div>}
        </>
    );
};

export default CarBookingRangePicker;
